import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { UserContext } from './UserContext';
import { apiFetch, getAccessToken } from '../utils/apiClient';

export const WishlistContext = createContext(null);

const HERO = 'heroCard';
const COLLECTIBLE = 'collectible';
const MARKETPLACE = 'marketplace';

function toIdSet(ids) {
    return new Set((ids || []).map(Number).filter((id) => Number.isFinite(id) && id > 0));
}

function resolveItemId(itemType, item) {
    if (!item) return NaN;
    if (itemType === HERO) {
        return Number(item.heroCardId ?? item.HeroCardId);
    }
    if (itemType === MARKETPLACE) {
        return Number(item.marketplaceItemId ?? item.MarketplaceItemId);
    }
    return Number(item.collectionItemId ?? item.CollectionItemId);
}

export const WishlistProvider = ({ children }) => {
    const { user } = useContext(UserContext);
    const [heroIds, setHeroIds] = useState(() => new Set());
    const [collectibleIds, setCollectibleIds] = useState(() => new Set());
    const [marketplaceIds, setMarketplaceIds] = useState(() => new Set());
    const [loaded, setLoaded] = useState(false);
    // Bumped on every toggle so in-flight ID loads cannot wipe optimistic UI.
    const mutationEpochRef = useRef(0);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            if (!user || !getAccessToken()) {
                setHeroIds(new Set());
                setCollectibleIds(new Set());
                setMarketplaceIds(new Set());
                setLoaded(false);
                return;
            }

            const epochAtStart = mutationEpochRef.current;

            try {
                const [heroRes, collectibleRes, marketplaceRes] = await Promise.all([
                    apiFetch('/HeroCard/GetUserHeroCardWishListIds', {
                        method: 'POST',
                        body: {},
                    }),
                    apiFetch('/Collectibles/GetUserCollectibleWishListIds', {
                        method: 'POST',
                        body: {},
                    }),
                    apiFetch('/Marketplace/GetUserMarketplaceWishListIds', {
                        method: 'POST',
                        body: {},
                    }),
                ]);

                if (cancelled) return;

                // A heart was toggled while this load was in flight — keep optimistic state.
                if (epochAtStart !== mutationEpochRef.current) {
                    setLoaded(true);
                    return;
                }

                const heroData = heroRes.ok ? await heroRes.json() : { ids: [] };
                let collectibleData = { ids: [] };
                if (collectibleRes.ok) {
                    collectibleData = await collectibleRes.json();
                } else {
                    console.warn('Collectible wishlist ids unavailable:', collectibleRes.status);
                }
                let marketplaceData = { ids: [] };
                if (marketplaceRes.ok) {
                    marketplaceData = await marketplaceRes.json();
                } else {
                    console.warn('Marketplace wishlist ids unavailable:', marketplaceRes.status);
                }

                if (cancelled || epochAtStart !== mutationEpochRef.current) {
                    setLoaded(true);
                    return;
                }

                setHeroIds(toIdSet(heroData.ids || heroData.Ids));
                setCollectibleIds(toIdSet(collectibleData.ids || collectibleData.Ids));
                setMarketplaceIds(toIdSet(marketplaceData.ids || marketplaceData.Ids));
                setLoaded(true);
            } catch (error) {
                console.error('Failed to load wishlist ids:', error);
                if (!cancelled && epochAtStart === mutationEpochRef.current) {
                    setHeroIds(new Set());
                    setCollectibleIds(new Set());
                    setMarketplaceIds(new Set());
                    setLoaded(true);
                }
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [user]);

    const isWishlisted = useCallback(
        (itemType, itemId) => {
            const id = Number(itemId);
            if (!Number.isFinite(id) || id <= 0) return false;
            if (itemType === HERO) return heroIds.has(id);
            if (itemType === COLLECTIBLE) return collectibleIds.has(id);
            if (itemType === MARKETPLACE) return marketplaceIds.has(id);
            return false;
        },
        [heroIds, collectibleIds, marketplaceIds]
    );

    const toggleWishlist = useCallback(
        async ({ itemType, item }) => {
            if (!user || !getAccessToken()) {
                return { ok: false, reason: 'auth' };
            }

            const id = resolveItemId(itemType, item);
            if (!Number.isFinite(id) || id <= 0) {
                console.warn('Wishlist toggle skipped: invalid item id', { itemType, item });
                return { ok: false, reason: 'invalid' };
            }

            const currentlyWishlisted = isWishlisted(itemType, id);
            const nextWishlisted = !currentlyWishlisted;

            const applyLocal = (add) => {
                const updater = (prev) => {
                    const next = new Set(prev);
                    if (add) next.add(id);
                    else next.delete(id);
                    return next;
                };
                if (itemType === HERO) setHeroIds(updater);
                else if (itemType === MARKETPLACE) setMarketplaceIds(updater);
                else setCollectibleIds(updater);
            };

            mutationEpochRef.current += 1;
            applyLocal(nextWishlisted);

            try {
                let response;
                if (itemType === HERO) {
                    if (nextWishlisted) {
                        response = await apiFetch('/HeroCard/AddHeroCardToWishList', {
                            method: 'POST',
                            body: {
                                HeroCardId: id,
                                Title: item.title || '',
                                ImageUrl: item.imageUrl || '',
                                DriverName: item.driver || item.driverName || '',
                                Description: item.description || '',
                                Series: item.series || '',
                                Year: item.year || '',
                                Sponsor: item.sponsor || '',
                                Team: item.team || null,
                            },
                        });
                    } else {
                        response = await apiFetch('/HeroCard/RemoveHeroCardFromWishList', {
                            method: 'POST',
                            body: { HeroCardId: id },
                        });
                    }
                } else if (itemType === MARKETPLACE) {
                    if (nextWishlisted) {
                        response = await apiFetch('/Marketplace/AddMarketplaceItemToWishList', {
                            method: 'POST',
                            body: {
                                MarketplaceItemId: id,
                                Title: item.title || item.Title || '',
                                ImageUrl: item.imageUrl || item.ImageUrl || '',
                                Driver: item.driver || item.driverName || item.Driver || '',
                                MainCategory: item.mainCategory || item.MainCategory || '',
                                Price: Number(item.price ?? item.Price) || 0,
                                ShippingAvailable: !!(item.shippingAvailable ?? item.ShippingAvailable),
                                City: item.city || item.City || '',
                                State: item.state || item.State || '',
                                SellerUserId: Number(item.userId ?? item.UserId) || 0,
                            },
                        });
                    } else {
                        response = await apiFetch('/Marketplace/RemoveMarketplaceItemFromWishList', {
                            method: 'POST',
                            body: { MarketplaceItemId: id },
                        });
                    }
                } else if (nextWishlisted) {
                    response = await apiFetch('/Collectibles/AddCollectibleToWishList', {
                        method: 'POST',
                        body: {
                            CollectionItemId: id,
                            Title: item.title || '',
                            ImageUrl: item.imageUrl || '',
                            Driver: item.driver || item.driverName || '',
                            MainCategory: item.mainCategory || item.MainCategory || '',
                        },
                    });
                } else {
                    response = await apiFetch('/Collectibles/RemoveCollectibleFromWishList', {
                        method: 'POST',
                        body: { CollectionItemId: id },
                    });
                }

                if (!response.ok) {
                    applyLocal(currentlyWishlisted);
                    console.warn('Wishlist toggle failed:', response.status, itemType, id);
                    return { ok: false, reason: 'api' };
                }

                // Re-assert after await so a late ID-load cannot leave the heart empty.
                applyLocal(nextWishlisted);
                return { ok: true, wishlisted: nextWishlisted };
            } catch (error) {
                console.error('Wishlist toggle failed:', error);
                applyLocal(currentlyWishlisted);
                return { ok: false, reason: 'api' };
            }
        },
        [user, isWishlisted]
    );

    const value = useMemo(
        () => ({
            loaded,
            isWishlisted,
            toggleWishlist,
            itemTypes: { HERO, COLLECTIBLE, MARKETPLACE },
        }),
        [loaded, isWishlisted, toggleWishlist]
    );

    return (
        <WishlistContext.Provider value={value}>
            {children}
        </WishlistContext.Provider>
    );
};

export function useWishlist() {
    const ctx = useContext(WishlistContext);
    if (!ctx) {
        throw new Error('useWishlist must be used within WishlistProvider');
    }
    return ctx;
}
